<template>
  <div
    v-if="!!attributeName"
    :id="`${attributeName}-attribute`">
    <v-data-table
      :headers="headers"
      :items="attributeData"
      :options.sync="options"
      :items-per-page="options.itemsPerPage"
      :loading="loading"
      :server-items-length="itemsLength"
      :height="530"
      class="elevation-1 mb-5"
      hide-default-footer
      @update:page="optionsUpdated()"
      @update:sort-by="optionsUpdated()"
      @update:sort-desc="optionsUpdated()">
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>
            <span class="capitalize">
              {{ attributeName }}
            </span>
          </v-toolbar-title>
          <v-divider
            class="mx-4"
            inset
            vertical />
          <v-spacer />
          <search-box
            v-model="search"
            @on-search="searchAttribute($event)" />
          <v-dialog
            v-model="dialog"
            max-width="500px">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                dark
                class="mb-1 ml-2"
                v-bind="attrs"
                v-on="on">
                New Item
              </v-btn>
            </template>
            <v-card>
              <validation-observer
                ref="attributeForm"
                tag="form"
                @submit.prevent="save()">
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>
                <v-card-text>
                  <v-container>
                    <v-row v-if="dialog">
                      <v-col
                        v-for="(form, index) in formData"
                        :key="index"
                        :cols="12">
                        <gw-text-field
                          v-model="formData[index].name"
                          :rules="textFieldRules"
                          :readonly="modalLoading"
                          label="Name" />
                      </v-col>
                    </v-row>
                    <v-row v-if="createMulti && !isEdited">
                      <v-spacer />
                      <v-col cols="auto">
                        <v-btn
                          color="blue darken-1"
                          text
                          @click="addForm()">
                          Add
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="closeFormModal()">
                    Cancel
                  </v-btn>
                  <v-btn
                    :loading="modalLoading"
                    color="blue darken-1"
                    type="submit"
                    text>
                    Save
                  </v-btn>
                </v-card-actions>
              </validation-observer>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <template
        #[`item.bcSync`]="{ item }"
        class="d-flex justify-center">
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-icon
              v-if="item.bcSync && item.bcSync.sync && item.bcSync.bcLog"
              v-bind="attrs"
              color="success"
              v-on="on">
              mdi-check-circle-outline
            </v-icon>
            <v-icon
              v-else
              v-bind="attrs"
              color="error"
              v-on="on">
              mdi-sync-alert
            </v-icon>
          </template>
          <span v-if="item.bcSync && item.bcSync.updatedAt">
            Last sync date {{ item.bcSync.updatedAt | dateTimeUserFormat() }}
          </span>
          <span v-else>
            Not sync yet
          </span>
        </v-tooltip>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-icon
          small
          class="mr-2"
          @click="editItem(item)">
          mdi-pencil
        </v-icon>
        <gw-icon-hover
          small
          class="sku-table-bin"
          icon-name="mdi-delete"
          icon-hover="mdi-delete-empty"
          @click="removeAttribute(item)" />
      </template>
    </v-data-table>
    <v-pagination
      v-if="paginationEnable"
      v-model="options.page"
      :length="pageCount"
      :disabled="loading"
      total-visible="7" />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'
import SearchBox from '@/components/SearchBox.vue'

const ProductAttributeService = new ProductAttributeProvider()

export default {
  components: { SearchBox },
  props: {
    attributeName: {
      type: String,
      requied: true,
      default: ''
    },
    createMulti: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialog: false,
      loading: false,
      itemsLength: 0,
      pageCount: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['id'],
        sortDesc: [true]
      },
      header: [
        {
          text: 'ID',
          value: 'id',
          width: 100
        },
        {
          text: 'Name',
          value: 'name',
          width: 200
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          width: 60,
          align: 'end'
        }
      ],
      attributeData: [],
      isEdited: false,
      formData: [{}],
      textFieldRules: {
        required: true
      },
      getting: false,
      modalLoading: false,
      isFocus: false,
      search: ''
    }
  },
  computed: {
    formTitle () {
      return this.isEdited ? 'Edit Item' : 'New Item'
    },
    paginationEnable () {
      return this.pageCount > 1
    },
    isExclueBC () {
      return ['tags'].includes(this.attributeName)
    },
    headers () {
      if (this.isExclueBC) {
        return this.header
      }

      return [
        {
          text: 'Sync',
          value: 'bcSync',
          width: 80
        },
        ...this.header
      ]
    }
  },
  mounted () {
    if (this.search === '') {
      this.search = this.$route.query?.search || ''
    }
    this.getAttribute(this.options)
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal'
    }),
    capitalize (value) {
      if (!value) {
        return ''
      }
      return value.toString().charAt(0).toUpperCase() + value.slice(1)
    },
    searchAttribute (value) {
      this.options.page = 1
      this.search = value

      this.$router.push({
        name: this.$route.name,
        query: { search: value }
      }).catch(() => {})
      this.getAttribute(this.options)
    },
    async getAttribute (options) {
      if (!this.getting) {
        try {
          this.getting = true
          this.loading = true
          const { data } = await ProductAttributeService.getProductAttribute(this.attributeName, {
            ...options,
            search: this.search
          })
          this.itemsLength = data.total
          this.pageCount = data.pages
          this.attributeData = data.results
        } catch (error) {
          console.error('getAttribute: ', error)
          this.setErrorPage(error.code)
        } finally {
          this.loading = false
          this.getting = false
        }
      }
    },
    optionsUpdated () {
      this.getAttribute(this.options)
    },
    addForm () {
      this.formData.push({})
    },
    closeFormModal () {
      this.dialog = false
      this.isEdited = false
      this.formData = [{}]
    },
    editItem (item) {
      this.isEdited = true
      this.formData = [{
        id: item.id,
        name: item.name
      }]
      this.dialog = true
    },
    async save () {
      this.modalLoading = true
      const vaid = await this.$refs.attributeForm.validate()
      if (vaid) {
        let isSuscess = false
        let message = 'created'
        try {
          if (this.isEdited) {
            isSuscess = await this.updateAttribute(this.formData[0])
            message = 'updated'
          } else {
            isSuscess = await this.createAttribute(this.formData)
          }
        } catch (error) {
          this.setSnackbar({
            value: true,
            message: `Error code ${error.code}: ${error.message}`,
            type: 'error'
          })
          console.error('saveAttribute', error)
        } finally {
          this.closeFormModal()
          if (isSuscess) {
            this.setSnackbar({
              value: true,
              message: this.$t(`snackbar.${this.capitalize(this.attributeName)} ${message}`),
              type: 'success'
            })
            this.getAttribute(this.options)
          }
        }
      }
      this.modalLoading = false
    },
    async createAttribute (items) {
      if (this.createMulti) {
        const payload = {}
        payload[this.attributeName] = items.map((r) => r.name.trim())
        const { data } = await ProductAttributeService.createMultiProductAttribute(this.attributeName, payload)
        return !data.some((r) => !r.id)
      }
      const { data } = await ProductAttributeService.createProductAttribute(this.attributeName, {
        name: items[0].name.trim()
      })
      return !!data.id
    },
    async updateAttribute (item) {
      const { data } = await ProductAttributeService.updateProductAttribute({
        path: this.attributeName,
        id: item.id
      }, {
        ...item,
        name: item.name.trim()
      })
      return !!data.id
    },
    async removeAttribute (item) {
      this.setModal({
        value: true,
        title: `Remove${this.capitalize(this.attributeName)}`,
        message: `Do you want to remove the ${this.attributeName}`,
        confirmText: 'Remove',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.confirmRemoveAttribute(item.id)
      })
    },
    async confirmRemoveAttribute (id) {
      try {
        const { data } = await ProductAttributeService.deleteAttribute(this.attributeName, id)
        if (data.id && data.status === 'deleted') {
          this.setSnackbar({
            value: true,
            message: this.$t(`snackbar.${this.capitalize(this.attributeName)} removed`),
            type: 'success'
          })
          if (this.options.page > 1 && this.attributeData.length < 2) {
            this.options.page--
          } else {
            this.getAttribute(this.options)
          }
        } else {
          const error = {
            code: 400,
            message: `${this.capitalize(this.attributeName)} ID not found or status not change`
          }
          throw error
        }
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: `Error code ${error.code}`,
          type: 'error'
        })
        console.error('confirmRemoveAttribute', error)
      }
    }
  }
}
</script>
